/**
 * Hand Resolution screen (05) — per PRD §8.3 + wireframe
 * `04_hand_resolution_*`.
 *
 * Shown after a hand terminates, before the Post-Hand Grade screen. Two
 * variants by terminal reason:
 *
 *   - `fold`               → minimal "FOLDED" banner + "Continue to grade".
 *                            No range matrix (PRD: "chips move … matter-of-
 *                            factly. No fanfare.")
 *   - `showdown` /
 *     `allin_runout`       → "SHOWDOWN" banner + full board + range matrix
 *                            with villain's actual hand highlighted (PRD §2:
 *                            villain hole cards are NEVER drawn as isolated
 *                            face-up cards — only as a matrix cell).
 *
 * "Continue to grade →" navigates to /grade.
 */

import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CardsRow } from '@/components/cards/Card';
import {
  FISH_PLACEHOLDER_RANGE,
  handNotation,
  RangeMatrix,
} from '@/components/RangeMatrix';
import { sessionManager } from '@/services/sessionManager';
import { isTerminal } from '@/lib/engine/stateMachine';
import type { HandState } from '@/types/handState';
import type { Session } from '@/types/session';
import { cn } from '@/lib/utils';

export function HandResolutionScreen() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setSession(sessionManager.loadActiveSession());
    setLoaded(true);
  }, []);

  if (!loaded) return null;
  if (!session) return <Navigate to="/" replace />;
  if (!session.activeHand) return <Navigate to="/config" replace />;
  if (!isTerminal(session.activeHand)) return <Navigate to="/play" replace />;

  const state = session.activeHand;
  const archetypeLabel = session.activeHandResolved?.archetype ?? 'Fish';

  return (
    <main className="flex min-h-dvh flex-col bg-surface-app text-text-primary">
      <Header
        potType={state.potType}
        heroPosition={state.heroPosition}
        archetype={archetypeLabel}
      />

      <div className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-4 pb-24 pt-4">
        <StatusBanner state={state} />
        <FeltDisplay
          state={state}
          archetypeLabel={archetypeLabel}
        />
        <ShowdownDetail state={state} archetypeLabel={archetypeLabel} />
      </div>

      {/* Continue CTA */}
      <div className="flex min-h-[5rem] items-center border-t border-border bg-surface-elevated px-4">
        <div className="mx-auto flex w-full max-w-2xl justify-end">
          <button
            type="button"
            onClick={() => navigate('/grade')}
            className={cn(
              'rounded-md bg-brand-primary px-5 py-2.5 text-sm font-semibold',
              'text-text-on-brand hover:bg-brand-primary-hover',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
            )}
          >
            Continue to grade →
          </button>
        </div>
      </div>
    </main>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function Header({
  potType,
  heroPosition,
  archetype,
}: {
  potType: HandState['potType'];
  heroPosition: HandState['heroPosition'];
  archetype: string;
}) {
  return (
    <div className="shrink-0 border-b border-border bg-surface-app px-4 py-3">
      <div className="mx-auto flex max-w-3xl items-center justify-center text-xs">
        <div className="text-text-secondary">
          {potType} <span className="text-text-muted">·</span> PFR{' '}
          <span className="text-text-muted">·</span> {heroPosition}{' '}
          <span className="text-text-muted">·</span> {archetype}
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ state }: { state: HandState }) {
  const label = (() => {
    switch (state.terminalReason) {
      case 'fold':
        return 'HAND COMPLETE  ·  FOLDED';
      case 'showdown':
        return 'HAND COMPLETE  ·  SHOWDOWN';
      case 'allin_runout':
        return 'HAND COMPLETE  ·  ALL-IN RUNOUT';
      default:
        return 'HAND COMPLETE';
    }
  })();
  return (
    <div className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-text-muted">
      {label}
    </div>
  );
}

function FeltDisplay({
  state,
  archetypeLabel,
}: {
  state: HandState;
  archetypeLabel: string;
}) {
  const heroIP = state.heroPosition === 'IP';
  const heroPosLabel = heroIP ? 'BTN' : 'BB';
  const villainPosLabel = heroIP ? 'BB' : 'BTN';

  const lastAction = state.history[state.history.length - 1];
  const heroFolded =
    state.terminalReason === 'fold' && lastAction?.actor === 'hero';

  return (
    <section className="mb-6 rounded-lg border border-border bg-surface-elevated p-4">
      {/* Villain — name + pill only; cards never shown (PRD §2) */}
      <div className="flex flex-col items-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-border-strong bg-surface-app text-sm font-bold">
          F
        </div>
        <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-text-secondary">
          {villainPosLabel} · Villain · {archetypeLabel}
        </div>
      </div>

      {/* Board (whatever was dealt) */}
      <div className="mt-4 flex justify-center">
        {state.board.length > 0 ? (
          <CardsRow cards={state.board} size="md" />
        ) : (
          <div className="text-xs text-text-muted">No board (pre-flop fold)</div>
        )}
      </div>

      {/* Pot */}
      <div className="mt-3 flex items-baseline justify-center gap-2">
        <span className="text-xl font-bold tabular-nums text-text-primary">
          ${state.pot.toLocaleString()}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-text-muted">
          Pot
        </span>
      </div>

      {/* Hero — name + stack + cards (cards faded if hero folded) */}
      <div className="mt-4 flex flex-col items-center">
        <div className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">
          {heroPosLabel} · You · ${state.stacks.hero.toLocaleString()}
        </div>
        <div
          className={cn(
            'mt-1 flex h-10 w-10 items-center justify-center rounded-full border-2 border-brand-accent bg-surface-app text-sm font-bold text-brand-accent',
            heroFolded && 'opacity-40',
          )}
        >
          H
        </div>
        <div className={cn('mt-2', heroFolded && 'opacity-40')}>
          <CardsRow cards={state.heroCards} size="md" />
        </div>
      </div>
    </section>
  );
}

function ShowdownDetail({
  state,
  archetypeLabel,
}: {
  state: HandState;
  archetypeLabel: string;
}) {
  // Range matrix is shown on every terminal — fold, showdown, all-in
  // runout. Per PRD §2 (Process Over Results), the matrix cell is the
  // ONLY place villain's actual hand is ever revealed, so omitting it on
  // fold would mean the player never sees what they made villain
  // discard. The fold caption uses villain's last action ("flop-fold",
  // "turn-fold", etc.) to describe the range at the point of fold.
  const actualNotation = handNotation(state.villainCards);
  const lastVillainAction = [...state.history]
    .reverse()
    .find((rec) => rec.actor === 'villain');
  const captionStreet = lastVillainAction?.street ?? state.street;
  const captionVerb = lastVillainAction
    ? actionVerbForCaption(lastVillainAction.action.kind)
    : 'shown';
  const combosPct = Math.round(
    (FISH_PLACEHOLDER_RANGE.size / 169) * 100,
  );

  return (
    <section className="mb-6">
      <h2 className="mb-1 text-xs font-bold uppercase tracking-wider text-text-secondary">
        Villain's expected range
      </h2>
      <p className="mb-1 text-xs text-text-secondary">
        {archetypeLabel} · {captionStreet}-{captionVerb} ·{' '}
        ≈{combosPct}% combos
      </p>
      <p className="mb-3 text-xs text-text-muted">
        Highlighted cell = actual hand ({actualNotation})
      </p>
      <RangeMatrix
        rangeNotations={FISH_PLACEHOLDER_RANGE as Set<string>}
        actualHandNotation={actualNotation}
      />
    </section>
  );
}

function actionVerbForCaption(kind: string): string {
  switch (kind) {
    case 'call':
      return 'call';
    case 'bet':
      return 'bet';
    case 'raise':
      return 'raise';
    case 'check':
      return 'check';
    case 'allin':
      return 'all-in';
    case 'fold':
      return 'fold';
    default:
      return 'shown';
  }
}
